import React, {useState} from 'react'

import {FormikErrors, useFormik} from 'formik'
import isEmpty from 'lodash/isEmpty'

import * as authAction from '../../actions/auth'
import * as modalAction from '../../actions/modal'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import useDispatch from '../../hooks/use-dispatch'
import authService from '../../services/auth'

import {ModalMeta} from '../../types'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import InputField from '../../../../commons/src/components/input-field'
import LinkButton from '../../../../commons/src/components/link-button'
import List from '../../../../commons/src/components/list'
import Modal from '../../../../commons/src/components/modal'
import Paragraph from '../../../../commons/src/components/paragraph'
import SuccessMessage from '../../../../commons/src/components/success-message'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'

type Props = {
  meta: ModalMeta
}

const AuthChangePasswordModal = ({meta}: Props) => {
  const dispatch = useDispatch()
  const [isDone, setIsDone] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      previousPassword: '',
      // Additional errors
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.required(values.previousPassword)) {
        errors.previousPassword = 'true'
      }

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      if (!validator.required(values.confirmPassword)) {
        errors.confirmPassword = 'true'
      }

      return errors
    },
    onSubmit: async (values, actions) => {
      if (values.password !== values.confirmPassword) {
        actions.setFieldError('general', 'Please make sure the provided new passwords are equal.')
        actions.setFieldError('password', 'true')
        actions.setFieldError('confirmPassword', 'true')
        return Promise.resolve()
      }

      return authService
        .getAuth()
        .then(async auth => {
          const currentAuthUser = await auth.currentAuthenticatedUser()
          return auth.changePassword(currentAuthUser, values.previousPassword, values.password)
        })
        .then(() => {
          setIsDone(true)
          dispatch(authAction.signOut(true))
        })
        .catch(error => {
          if (
            error.message.match(/previousPassword/gi) &&
            error.code === 'InvalidParameterException'
          ) {
            actions.setFieldError('previousPassword', 'Your previous password is incorrect.')
          } else if (
            (error.code === 'InvalidParameterException' ||
              error.code === 'InvalidPasswordException') &&
            error.message.match(/password/gi)
          ) {
            actions.setFieldError('password', 'true')
            actions.setFieldError('confirmPassword', 'true')
            actions.setFieldError(
              'general',
              'Your password must have a minimum length of 8 characters and contain a combination of upper-case, lower-case and digits.'
            )
          } else {
            actions.setFieldError('general', error.message)
          }
        })
    },
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      size="s"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Change password" size="l" />}
      onClose={() => dispatch(modalAction.close())}
    >
      {!isDone && (
        <form onSubmit={formik.handleSubmit}>
          {formik.errors.general && <Paragraph error>{formik.errors.general}</Paragraph>}
          <FormRow>
            <InputField
              name="previousPassword"
              label="Previous password"
              autoComplete="off"
              type="password"
              showPassword
              autoFocus
              error={formik.submitCount > 0 && !!formik.errors.previousPassword}
              onChange={(v, n, event) => formik.handleChange(event)}
              value={formik.values.previousPassword}
              onBlur={formik.handleBlur}
            />
          </FormRow>
          <FormRow>
            <FieldNote
              note={
                <TooltipHandler
                  tooltip={
                    <Tooltip
                      footer={
                        <List>
                          <li>
                            have a minimum <strong>length of 8 characters</strong>
                          </li>
                          <li>
                            contain at least <strong>one digit</strong>
                          </li>
                          <li>
                            contain at least <strong>one upper-case character</strong>
                          </li>
                          <li>
                            contain at least <strong>one lower-case character</strong>
                          </li>
                        </List>
                      }
                    >
                      Your password must…
                    </Tooltip>
                  }
                >
                  <LinkButton>Show password requirements</LinkButton>
                </TooltipHandler>
              }
            >
              <InputField
                type="password"
                showPassword
                name="password"
                label="New password"
                autoComplete="new-password"
                error={formik.submitCount > 0 && !!formik.errors.password}
                onChange={(v, n, event) => formik.handleChange(event)}
                value={formik.values.password}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
          </FormRow>
          <FormRow>
            <InputField
              type="password"
              label="Repeat password"
              name="confirmPassword"
              showPassword
              autoComplete="off"
              error={formik.submitCount > 0 && !!formik.errors.confirmPassword}
              onChange={(v, n, event) => formik.handleChange(event)}
              value={formik.values.confirmPassword}
              onBlur={formik.handleBlur}
            />
          </FormRow>
          <Paragraph minor size="s">
            After changing your password you will be signed out from all your devices and you will
            have to sign in again.
          </Paragraph>
          <Button
            block
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Continue
          </Button>
        </form>
      )}
      {isDone && (
        <SuccessMessage>
          You have successfully updated your password! Please sign in again.
        </SuccessMessage>
      )}
    </Modal>
  )
}

export default AuthChangePasswordModal
